import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import RequireAuth from "./components/common/auth/RequireAuth";
import AppLayout from "./components/common/layout/AppLayout";
import Login from "./routes/Public/Login";
import ForgotPassword from "./routes/Public/ForgotPassword";
import AcceptInvite from "./routes/Public/AcceptInvite";
import StoreSelection from "./routes/Public/StoreSelection";
import PrivateLayout from "./routes/Private";
import NotFound from "./routes/NotFound";
import ServerDown from "./routes/ServerDown";
import AccessDenied from "./routes/AccessDenied";
import { hasAccessByOptions } from "./helpers/helper";
import { ConfigProvider } from "antd";
import { Toaster } from "sonner";

const theme = {
  token: {
    colorPrimary: "#3D1A07",
    colorSuccess: "#4A7C59",
    colorWarning: "#C4882A",
    colorError: "#B5511C",
    colorInfo: "#4A6FA5",
    borderRadius: 8,
    fontFamily: "'Inter', sans-serif",
    colorBgContainer: "#FDFAF4",
    colorBgLayout: "#F7EFE0",
    colorBgElevated: "#FDFAF4",
    colorText: "#3D1A07",
    colorTextSecondary: "#7A4A2A",
    colorBorder: "#E0CEAF",
    colorBorderSecondary: "#DDD0BC",
    colorLink: "#7A4A2A",
    colorLinkHover: "#3D1A07",
  },
  components: {
    Table: {
      headerBg: "#F7EFE0",
      headerColor: "#7A4A2A",
      borderColor: "#E0CEAF",
      rowHoverBg: "#F7EFE0",
    },
    Drawer: {
      colorBgElevated: "#FDFAF4",
    },
    Card: {
      colorBgContainer: "#FDFAF4",
    }
  }
};

function ProtectedShell({ children }) {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }) {
  const { user } = useAuth();
  if (user) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  const isPublicShell =
    location.pathname === "/login" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/accept-invite" ||
    location.pathname === "/store-selection";

  return (
    <>
      {isPublicShell ? (
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/accept-invite" element={<PublicRoute><AcceptInvite /></PublicRoute>} />
          <Route path="/store-selection" element={<StoreSelection />} />
          <Route path="/server-down" element={<ServerDown />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      ) : (
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/accept-invite" element={<PublicRoute><AcceptInvite /></PublicRoute>} />
          <Route path="/store-selection" element={<StoreSelection />} />
          <Route path="/access-denied" element={<AccessDenied />} />
          <Route element={<RequireAuth />}>
            <Route path="/*" element={<ProtectedShell><PrivateLayout /></ProtectedShell>} />
          </Route>
        </Routes>
      )}
    </>
  );
}

export default function App() {
  return (
    <ConfigProvider theme={theme}>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          closeButton
          position="top-right"
          gap={10}
          toastOptions={{
            style: { fontFamily: "'Inter', sans-serif" },
          }}
        />
      </AuthProvider>
    </ConfigProvider>
  );
}